import { isReadOnlyRole } from '../config/roles';
import { tableName } from '../components/env';

const READ_ONLY_RPC_ALLOWLIST = new Set([
  'analytics_user_role',
  'analytics_user_schema',
]);
const READ_ONLY_TABLE_METHODS = new Set(['insert', 'update', 'delete', 'upsert']);
const READ_ONLY_STORAGE_METHODS = new Set([
  'upload',
  'update',
  'uploadToSignedUrl',
  'remove',
  'move',
  'copy',
]);

// Patients manage their own visit entries from the patient portal (add /
// edit appointment), so insert+update on the schedules table are allowed for
// the real patient role. Deletes, other tables, RPCs and storage stay
// blocked, as do all writes while the role is still unknown at bootstrap.
// Marks a storage write the guard has replaced with a thrower. The storage
// overlay (rdsStorageOverlay.js) sits ABOVE this proxy and, while RDS owns a
// bucket, no longer calls the Supabase method -- it checks this mark first
// and calls the thrower, so a blocked role still throws exactly as before
// and RDS is never reached. Symbol.for so supabaseClient.js's view-as block
// and the overlay share it without importing each other.
export const BLOCKED_STORAGE_WRITE = Symbol.for('notesai.storage.blockedWrite');

export function markBlockedStorageWrite(fn) {
  try {
    Object.defineProperty(fn, BLOCKED_STORAGE_WRITE, { value: true });
  } catch (_) {
    // a frozen function cannot be marked; the overlay then calls Supabase's
    // method as before, which is the thrower anyway
  }
  return fn;
}

const PATIENT_WRITABLE_TABLES = new Set([tableName]);
const PATIENT_ALLOWED_TABLE_METHODS = new Set(['insert', 'update']);

class PatientReadOnlyError extends Error {
  constructor(detail) {
    super(`patient role is read-only: ${detail}`);
    this.name = 'PatientReadOnlyError';
  }
}

class SupabaseRoleLoadingError extends Error {
  constructor(detail) {
    super(`supabase role is still loading: ${detail}`);
    this.name = 'SupabaseRoleLoadingError';
  }
}

function buildReadOnlyProxy(client, makeError, { allowPatientScheduleWrites = false } = {}) {
  return new Proxy(client, {
    get(target, prop) {
      // Cross-schema rows are written through client.schema(rowSchema) (see
      // utils/rowSchema.js). supabase-js implements that as `this.rest.schema()`,
      // which returns a bare PostgrestClient — so without this passthrough the
      // scoped client escapes every guard below. Re-wrap it.
      if (prop === 'schema') {
        return (name) =>
          buildReadOnlyProxy(target.schema(name), makeError, {
            allowPatientScheduleWrites,
          });
      }
      if (prop === 'from') {
        return (table) => {
          const builder = target.from(table);
          return new Proxy(builder, {
            get(b, key) {
              if (
                allowPatientScheduleWrites &&
                PATIENT_WRITABLE_TABLES.has(table) &&
                PATIENT_ALLOWED_TABLE_METHODS.has(key)
              ) {
                return Reflect.get(b, key);
              }
              if (READ_ONLY_TABLE_METHODS.has(key)) {
                return () => {
                  throw makeError(`${String(key)} on '${table}' blocked`);
                };
              }
              return Reflect.get(b, key);
            },
          });
        };
      }
      if (prop === 'rpc') {
        return (name, args) => {
          if (READ_ONLY_RPC_ALLOWLIST.has(name)) return target.rpc(name, args);
          throw makeError(`rpc('${name}') blocked`);
        };
      }
      if (prop === 'storage') {
        // A schema-scoped PostgrestClient has no storage — nothing to guard.
        if (!target.storage) return target.storage;
        return new Proxy(target.storage, {
          get(storageTarget, key) {
            if (key === 'from') {
              return (bucket) => {
                const bucketClient = storageTarget.from(bucket);
                return new Proxy(bucketClient, {
                  get(b, bucketKey) {
                    if (READ_ONLY_STORAGE_METHODS.has(bucketKey)) {
                      return markBlockedStorageWrite(() => {
                        throw makeError(
                          `storage.${String(bucketKey)} on '${bucket}' blocked`,
                        );
                      });
                    }
                    return Reflect.get(b, bucketKey);
                  },
                });
              };
            }
            return Reflect.get(storageTarget, key);
          },
        });
      }
      return Reflect.get(target, prop);
    },
  });
}

export function wrapSupabaseClientForReadOnlyRole(client, role, options = {}) {
  const shouldBlockUnknownRole =
    options.blockWritesWhileRoleUnknown === true && role == null;

  if (!isReadOnlyRole(role) && !shouldBlockUnknownRole) return client;

  return buildReadOnlyProxy(
    client,
    shouldBlockUnknownRole
      ? (detail) => new SupabaseRoleLoadingError(detail)
      : (detail) => new PatientReadOnlyError(detail),
    // Only a confirmed patient role gets the schedule-table write allowance;
    // while the role is unknown at bootstrap every write stays blocked.
    { allowPatientScheduleWrites: !shouldBlockUnknownRole },
  );
}
